import React, { useState, useEffect, useCallback, useRef } from 'react';
import { QUOTES } from '../data';
import { IconPlus, IconTrash, IconUser } from './Icons';
import { saveField, loadUserData } from '../supabase';
import HomeScreenBanner from './HomeScreenBanner';

const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const today = new Date();
const PLACEHOLDER_HABITS = [
  { id: 'ph1', name: 'Your first habit', placeholder: true },
  { id: 'ph2', name: 'Your second habit', placeholder: true },
  { id: 'ph3', name: 'Your third habit', placeholder: true },
];

export default function Today({ onNavigate, userId }) {
  const [activeMonth, setActiveMonth] = useState(today.getMonth());
  const [customHabits, setCustomHabits] = useState([]);
  const [trackerData, setTrackerData] = useState({});
  const [intentions, setIntentions] = useState([]); // loaded alongside habits so practices can be tied to goals
  const [showAddHabit, setShowAddHabit] = useState(false);
  const [newHabit, setNewHabit] = useState({ name: '', started: '', when: '', frequency: '', intentionId: '', why: '' });
  const [loading, setLoading] = useState(true);

  // Ref to the horizontally-scrollable tracker container so we can auto-scroll
  // to today's column on mount. Without this, mobile users land on day 1 and
  // have to manually scroll right to find today's column.
  const trackerRef = useRef(null);

  const quote = QUOTES[today.getDate() % QUOTES.length];
  const todayNum = today.getDate();
  const displayHabits = customHabits.length > 0 ? customHabits : PLACEHOLDER_HABITS;
  const doneTodayCount = customHabits.filter(h => trackerData[`${h.id}_${activeMonth}_${todayNum}`]).length;
  const pct = customHabits.length ? Math.round((doneTodayCount / customHabits.length) * 100) : 0;

  // Most-recent intention for the "currently practicing toward" card above the tracker.
  const activeIntention = intentions.length > 0 ? intentions[intentions.length - 1] : null;

  useEffect(() => {
    async function load() {
      setLoading(true);
      if (userId) {
        const data = await loadUserData(userId);
        if (data) {
          setCustomHabits(data.habits || []);
          setTrackerData(data.tracker || {});
          setIntentions(data.goals || []);
        }
      } else {
        try { setCustomHabits(JSON.parse(localStorage.getItem('bhn_custom_habits') || '[]')); } catch {}
        const saved = {};
        try {
          const stored = JSON.parse(localStorage.getItem('bhn_tracker') || '{}');
          Object.assign(saved, stored);
        } catch {}
        setTrackerData(saved);
        try { setIntentions(JSON.parse(localStorage.getItem('bhn_journal_goals') || '[]')); } catch {}
      }
      setLoading(false);
    }
    load();
  }, [userId]);

  // Auto-scroll the tracker so today's column is visible on mount.
  // Only fires when:
  //   • loading just finished (tracker is now rendered)
  //   • the user is viewing the current month (otherwise no "today" exists)
  // We center today's column horizontally instead of scrolling to the right edge,
  // so users see a few days before and after today for context.
  useEffect(() => {
    if (loading) return;
    if (activeMonth !== today.getMonth()) return;
    const wrap = trackerRef.current;
    if (!wrap) return;

    // Find today's column element (the <th> with today-col class).
    const todayCell = wrap.querySelector('th.today-col');
    if (!todayCell) return;

    // Center it horizontally inside the scrollable wrap.
    const wrapWidth = wrap.clientWidth;
    const cellLeft = todayCell.offsetLeft;
    const cellWidth = todayCell.offsetWidth;
    const target = cellLeft - (wrapWidth / 2) + (cellWidth / 2);
    wrap.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [loading, activeMonth]);

  const saveHabits = useCallback(async (habits) => {
    if (userId) await saveField(userId, 'habits', habits);
    else localStorage.setItem('bhn_custom_habits', JSON.stringify(habits));
  }, [userId]);

  const saveTracker = useCallback(async (tracker) => {
    if (userId) await saveField(userId, 'tracker', tracker);
    else localStorage.setItem('bhn_tracker', JSON.stringify(tracker));
  }, [userId]);

  function toggleCell(habitId, day) {
    const k = `${habitId}_${activeMonth}_${day}`;
    const newVal = !trackerData[k];
    const updated = { ...trackerData, [k]: newVal };
    setTrackerData(updated);
    saveTracker(updated);
  }

  function addCustomHabit() {
    if (!newHabit.name.trim()) return;
    const h = { ...newHabit, id: `custom_${Date.now()}` };
    const updated = [...customHabits, h];
    setCustomHabits(updated);
    saveHabits(updated);
    setNewHabit({ name: '', started: '', when: '', frequency: '', intentionId: '', why: '' });
    setShowAddHabit(false);
  }

  function deleteCustomHabit(id) {
    const updated = customHabits.filter(h => h.id !== id);
    setCustomHabits(updated);
    saveHabits(updated);
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <div style={{ fontSize: 13, color: '#A09888' }}>Loading your practice...</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <div className="top-bar" style={{ position: 'relative' }}>
        <div className="brand-small">Today</div>
        <div className="screen-title">Your Practice</div>
        <button
          onClick={() => onNavigate('profile')}
          aria-label="Open profile"
          style={{
            position: 'absolute', top: 14, right: 18,
            width: 36, height: 36, borderRadius: '50%',
            background: '#F5F3F0', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 0,
          }}
        >
          <IconUser size={17} color="#555" />
        </button>
      </div>
      <div className="screen-body">
        <HomeScreenBanner userId={userId} />
        <div className="quote-card">
          <div className="quote-text">{quote.text}</div>
          {quote.author && <div className="quote-author">— {quote.author}</div>}
        </div>

        {/* Active intention — the current 'why' behind today's practice.
            Tapping it jumps to the Intentions tab for review/editing. */}
        {activeIntention && (
          <div
            onClick={() => onNavigate('goals')}
            style={{
              background: '#F8FCF6', border: '1px solid #E8F0E4',
              borderRadius: 12, padding: '14px 16px', marginBottom: 20,
              cursor: 'pointer', transition: 'all .15s',
            }}
          >
            <div style={{ fontSize: 10, letterSpacing: '.12em', color: '#3A6A2A', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>
              Practicing toward
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111', lineHeight: 1.5, marginBottom: 4 }}>
              {activeIntention.goal}
            </div>
            {activeIntention.timeline && (
              <div style={{ fontSize: 11, color: '#777' }}>
                {activeIntention.timeline}
              </div>
            )}
          </div>
        )}

        {customHabits.length > 0 && (
          <>
            <div className="prog-wrap">
              <div className="prog-row">
                <span className="prog-text">Today's practice</span>
                <span className="prog-pct">{pct}%</span>
              </div>
              <div className="prog-bg"><div className="prog-fill" style={{ width: `${pct}%` }} /></div>
            </div>
            <div className="stats-grid">
              <div className="stat-card"><div className="stat-num">{doneTodayCount}</div><div className="stat-label">Done</div></div>
              <div className="stat-card"><div className="stat-num">{customHabits.length}</div><div className="stat-label">Practices</div></div>
              <div className="stat-card"><div className="stat-num">{pct}%</div><div className="stat-label">Complete</div></div>
            </div>
          </>
        )}

        <div className="section-label">Your Practices</div>
        <div className="month-pills">
          {MONTHS.map((m, i) => (
            <div key={m} className={`month-pill ${activeMonth === i ? 'active' : ''}`} onClick={() => setActiveMonth(i)}>{m}</div>
          ))}
        </div>

        <div className="tracker-wrap" ref={trackerRef}>
          <table className="tracker-table">
            <thead>
              <tr>
                <th className="habit-col">PRACTICE</th>
                {DAYS.map(d => {
                  const isToday = activeMonth === today.getMonth() && d === todayNum;
                  return (
                    <th key={d} className={isToday ? 'today-col' : ''} style={{ minWidth: 28 }}>
                      {isToday ? <span className="today-pill">{d}</span> : d}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {displayHabits.map(h => {
                // Link each practice to its connected intention (if any) so we can
                // show a subtle indicator in the tracker row.
                const linked = h.intentionId && intentions.find(g => String(g.id) === String(h.intentionId));
                return (
                <tr key={h.id}>
                  <td className="habit-name-cell" style={{ opacity: h.placeholder ? .35 : 1, position: 'sticky', left: 0, background: '#FFFFFF', zIndex: 2, minWidth: 130, maxWidth: 130 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {linked && (
                            <span title={`Serves: ${linked.goal}`} style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A6A2A', flexShrink: 0 }} />
                          )}
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#111', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3 }}>{h.name}</span>
                        </div>
                        {!h.placeholder && h.why && (
                          <div style={{ fontSize: 9, color: '#A09888', marginTop: 2, lineHeight: 1.3, fontStyle: 'italic' }}>
                            {h.why}
                          </div>
                        )}
                      </div>
                      {!h.placeholder && (
                        <button onClick={() => deleteCustomHabit(h.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', opacity: .35, flexShrink: 0, marginTop: 1 }}>
                          <IconTrash size={11} color="#888" />
                        </button>
                      )}
                    </div>
                  </td>
                  {DAYS.map(d => (
                    <td key={d}>
                      <div
                        className={`tracker-check ${!h.placeholder && trackerData[`${h.id}_${activeMonth}_${d}`] ? 'checked' : ''}`}
                        style={{ opacity: h.placeholder ? .25 : 1, cursor: h.placeholder ? 'default' : 'pointer' }}
                        onClick={() => !h.placeholder && toggleCell(h.id, d)}
                      >
                        {!h.placeholder && trackerData[`${h.id}_${activeMonth}_${d}`] && (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M5 12l5 5L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {customHabits.length === 0 && (
          <div style={{ fontSize: 12, color: '#C8C4BE', textAlign: 'center', marginBottom: 16, fontStyle: 'italic' }}>
            Begin with one small practice. Browse the library or add your own.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button className="btn-secondary" style={{ flex: 1, marginBottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onClick={() => setShowAddHabit(true)}>
            <IconPlus size={13} color="#333" /> Add practice
          </button>
          <button className="btn-secondary" style={{ flex: 1, marginBottom: 0 }} onClick={() => onNavigate('library')}>Browse library</button>
        </div>

        {showAddHabit && (
          <div style={{ borderTop: '1px solid #F5F3F0', paddingTop: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 20, color: '#111', letterSpacing: '-.3px' }}>Add a daily practice</div>
            <div className="input-label">My practice *</div>
            <input className="form-input" placeholder="e.g. Morning meditation" value={newHabit.name} onChange={e => setNewHabit(p => ({ ...p, name: e.target.value }))} />
            <div className="input-label">Date started</div>
            <input className="form-input" type="date" value={newHabit.started} onChange={e => setNewHabit(p => ({ ...p, started: e.target.value }))} />
            <div className="input-label">When (time of day)</div>
            <input className="form-input" placeholder="e.g. 7:00 AM" value={newHabit.when} onChange={e => setNewHabit(p => ({ ...p, when: e.target.value }))} />
            <div className="input-label">How often</div>
            <input className="form-input" placeholder="e.g. Once a day" value={newHabit.frequency} onChange={e => setNewHabit(p => ({ ...p, frequency: e.target.value }))} />
            {intentions.length > 0 && (
              <>
                <div className="input-label">Serves which intention? (optional)</div>
                <select value={newHabit.intentionId} onChange={e => setNewHabit(p => ({ ...p, intentionId: e.target.value }))}
                  style={{ width: '100%', padding: '10px 0', border: 'none', borderBottom: '1px solid #E8E4DF', background: 'transparent', fontSize: 13, color: '#333', marginBottom: 12, fontFamily: 'inherit', cursor: 'pointer' }}>
                  <option value="">Not connected to an intention</option>
                  {intentions.map(g => <option key={g.id} value={g.id}>{g.goal}</option>)}
                </select>
              </>
            )}
            <div className="input-label">Why are you committing to this? (optional)</div>
            <input className="form-input" placeholder="Your reason — just a few words" value={newHabit.why} onChange={e => setNewHabit(p => ({ ...p, why: e.target.value }))} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn-primary" style={{ flex: 1, marginBottom: 0 }} onClick={addCustomHabit}>Add practice</button>
              <button className="btn-secondary" style={{ flex: 1, marginBottom: 0 }} onClick={() => setShowAddHabit(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
